import { useState, useEffect, useContext } from "react";
import "./accountBillingPage.css";
import billingRequest from "../../../request/billing";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import BillingPageSkeleton from "./billingPageSkeleton/billingPageSkeleton";
import BannerMessage from "../../common/bannerMessage/bannerMessage";

const AccountBillingPage = () => {
	const { userData, language } = useContext(CombinedContext);
	const [billing, setBilling] = useState(null);
	const [loading, setLoading] = useState(true);
	const [referral, setReferral] = useState(null);
	const [refLoading, setRefLoading] = useState(true);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [referralInput, setReferralInput] = useState("");
	const [referralUpdating, setReferralUpdating] = useState(false);

	const shopId = userData.shop.shopId;
	const t = translations[language].billing;

	useEffect(() => {
		if (!shopId) {
			showBanner("error", t.errorLoading);
			setLoading(false);
			return;
		}
		loadBilling();
	}, [shopId]);
	useEffect(() => {
		if (userData?.user?.userId) loadReferral();
	}, [userData]);

	const loadReferral = async () => {
		try {
			const res = await billingRequest.getReferralDashboard(userData.user.userId, userData.cognitoTokens.AccessToken);
			setReferral(res);
		} catch (err) {
			console.error("Failed to load referral dashboard:", err);
			showBanner("error", t.referral.errorGenerate || "Error loading referral");
		} finally {
			setRefLoading(false);
		}
	};

	const loadBilling = async () => {
		try {
			const res = await billingRequest.getBillingInfo(shopId, userData.cognitoTokens.AccessToken);
			const sub = res.billing?.subscription;
			let normalizedSub = null;

			if (sub) {
				const items = (sub.items || []).map((item) => {
					const unitPrice = item.amount ? Number(item.amount) / 100 : 0;
					const quantity = item.quantity ?? 1;
					return {
						priceId: item.priceId,
						productName: item.productName || "Suscripción Sin Fila",
						unitPrice,
						quantity,
						total: unitPrice * quantity,
						currency: item.currency,
						interval: item.interval,
					};
				});

				normalizedSub = {
					items,
					grandTotal: items.reduce((sum, i) => sum + i.total, 0).toFixed(2),
					frequency: sub.billingCycle?.frequency || 1,
					interval: sub.billingCycle?.interval || "month",
					status: sub.status,
					currency: sub.currency,
					nextBillingDate: sub.nextBillingDate,
					firstBillingDate: sub.firstBillingDate,
					startedAt: sub.startedAt,
					activeDiscount: sub.activeDiscount || null,
					managementUrls: {
						update_payment_method: sub.managementUrls?.update_payment_method,
						cancel: sub.managementUrls?.cancel,
					},
				};
			}

			setBilling({ ...res.billing, subscription: normalizedSub });
		} catch (err) {
			console.error("Failed to load billing info:", err);
			showBanner("error", t.errorLoading);
		} finally {
			setLoading(false);
		}
	};

	const generateCode = async () => {
		try {
			await billingRequest.generateReferralCode({ userId: userData.user.userId, accessToken: userData.cognitoTokens.AccessToken });
			loadReferral();
		} catch (err) {
			showBanner("error", "Unable to generate referral code");
		}
	};

	const updateReferral = async () => {
		try {
			setReferralUpdating(true);
			await billingRequest.updateReferral({ code: referralInput || null, accessToken: userData.cognitoTokens.AccessToken });
			showBanner("success", t.referral.applied);
			loadReferral();
		} catch (err) {
			showBanner("error", err.message || "Failed to update referral");
		} finally {
			setReferralUpdating(false);
		}
	};

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	if (loading) return <BillingPageSkeleton />;

	const subscription = billing?.subscription || null;
	const upcomingPayment = billing?.upcomingPayment || null;
	const billingReferral = billing?.referral || null;
	const transactions = billing?.transactions || [];

	return (
		<div className="sf-billingpage">
			<div className="sf-billing-content">
				{/* ── LEFT COLUMN ── */}
				<div className="sf-billing-main-part">
					{/* Subscription */}
					<div className="sf-billing-card">
						<h2 className="sf-section-title">{t.currentSubscription}</h2>

						{subscription ? (
							<div className="sf-sub-row">
								<div className="sf-billing-info">
									{subscription.items.map((item, idx) => (
										<div key={idx} className="sf-sub-item">
											<div className="sf-billing-sub-row">
												<span className="sf-sub-plan">{item.productName}</span>
												<span className="sf-sub-unit">
													{item.quantity} × {subscription.currency} {item.unitPrice.toFixed(2)}
												</span>
											</div>
											<div className="sf-sub-item-total">
												{subscription.currency} {item.total.toFixed(2)}/{item.interval}
											</div>
										</div>
									))}

									<div className="sf-sub-status">
										<span className={`sf-badge sf-badge-${subscription.status}`}>{t.statusBadge[subscription.status]}</span>
									</div>

									{subscription.nextBillingDate && (
										<div className="sf-sub-next">
											{t.nextBillingOn.replace("{date}", new Date(subscription.nextBillingDate).toLocaleDateString())}
										</div>
									)}
								</div>

								{subscription.activeDiscount && (
									<div className="sf-discount-banner">
										<span className="sf-discount-label">{t.discount.active}</span>
										<span className="sf-discount-detail">
											{t.discount.appliedRange
												.replace("{from}", new Date(subscription.activeDiscount.startsAt).toLocaleDateString())
												.replace("{to}", new Date(subscription.activeDiscount.endsAt).toLocaleDateString())}
										</span>
									</div>
								)}

								<div className="sf-sub-total">
									<strong>Total:</strong> {subscription.currency} {subscription.grandTotal}/{subscription.interval}
								</div>

								<div className="sf-sub-actions">
									<button
										className="sf-btn sf-btn-outline"
										onClick={() => window.open(subscription.managementUrls?.update_payment_method, "_blank")}
									>
										{t.buttons.updatePaymentMethod}
									</button>
									<button className="sf-btn sf-btn-warning" onClick={() => window.open(subscription.managementUrls?.cancel, "_blank")}>
										{t.buttons.cancelSubscription}
									</button>
								</div>
							</div>
						) : (
							<div>
								{/* <p>{t.noSubscription}</p> */}

								{/* Apply referral code inline — no nested card */}
								<div className="sf-referral-apply-block">
									<p className="sf-referral-apply-title">{t.referral.applyTitle}</p>
									<div className="sf-referral-input-row">
										<input
											type="text"
											placeholder={t.referral.applyPlaceholder}
											value={referralInput}
											onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
											className="sf-input"
										/>
										<button className="sf-btn sf-btn-primary" onClick={updateReferral} disabled={referralUpdating}>
											{referralUpdating ? t.referral.applyLoading : t.referral.applyButton}
										</button>
									</div>
									{userData?.user?.tempReferralCode && (
										<div className="sf-referral-current">
											{t.referral.usingCode} <strong>{userData.user.tempReferralCode}</strong>
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Upcoming Payment */}
					{upcomingPayment && (
						<div className="sf-billing-card">
							<h3 className="sf-section-title">{t.upcomingPayment.title}</h3>
							<div className="sf-upcoming-grid">
								<div className="sf-upcoming-row">
									<span className="sf-info-label">{t.upcomingPayment.date}</span>
									<span>{new Date(upcomingPayment.date).toLocaleDateString()}</span>
								</div>
								<div className="sf-upcoming-row">
									<span className="sf-info-label">{t.upcomingPayment.subtotal}</span>
									<span>
										{upcomingPayment.currency} {(upcomingPayment.subtotalCents / 100).toFixed(2)}
									</span>
								</div>
								{upcomingPayment.discountCents > 0 && (
									<div className="sf-upcoming-row sf-upcoming-discount">
										<span className="sf-info-label">{t.upcomingPayment.discount}</span>
										<span>
											− {upcomingPayment.currency} {(upcomingPayment.discountCents / 100).toFixed(2)}
										</span>
									</div>
								)}
								<div className="sf-upcoming-row sf-upcoming-total">
									<span>
										<strong>{t.upcomingPayment.totalDue}</strong>
									</span>
									<span>
										<strong>
											{upcomingPayment.currency} {(upcomingPayment.totalCents / 100).toFixed(2)}
										</strong>
									</span>
								</div>
							</div>
						</div>
					)}

					{/* Transaction History */}
					{transactions.length > 0 && (
						<div className="sf-billing-card">
							<h3 className="sf-section-title">{t.transactions.title}</h3>
							<table className="sf-txn-table">
								<thead>
									<tr>
										<th>{t.transactions.date}</th>
										<th>{t.transactions.invoice}</th>
										<th>{t.transactions.subtotal}</th>
										<th>{t.transactions.discount}</th>
										<th>{t.transactions.total}</th>
										<th>{t.transactions.status}</th>
									</tr>
								</thead>
								<tbody>
									{transactions.map((txn, i) => (
										<tr key={i}>
											<td data-label={t.transactions.date}>
												{txn.billedAt ? new Date(txn.billedAt).toLocaleDateString() : t.transactions.noInvoice}
											</td>
											<td data-label={t.transactions.invoice} className="sf-txn-invoice">
												{txn.invoiceNumber ? `#${txn.invoiceNumber}` : t.transactions.noInvoice}
											</td>
											<td data-label={t.transactions.subtotal}>
												{txn.currency} {(parseInt(txn.subtotal || "0") / 100).toFixed(2)}
											</td>
											<td data-label={t.transactions.discount} className="sf-txn-discount-cell">
												{parseInt(txn.discount || "0") > 0
													? `− ${txn.currency} ${(parseInt(txn.discount) / 100).toFixed(2)}`
													: t.transactions.noInvoice}
											</td>
											<td data-label={t.transactions.total}>
												<strong>
													{txn.currency} {(parseInt(txn.total || "0") / 100).toFixed(2)}
												</strong>
											</td>
											<td data-label={t.transactions.status}>
												<span className={`sf-badge sf-badge-${txn.status}`}>{t.statusBadge[txn.status] || txn.status}</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				{/* ── RIGHT SIDEBAR ── */}
				<div className="sf-billing-sidebar">
					{/* Payment Method */}
					<div className="sf-billing-card sf-sidebar-card">
						<h3 className="sf-sidebar-title">{t.paymentMethod.title}</h3>
						{billing?.customer?.paymentMethod ? (
							<div className="sf-payment-method-card">
								<div className="sf-payment-method-line">
									<span className="sf-payment-label">{t.paymentMethod.brand}</span>
									<span className="sf-pm-brand">{billing.customer.paymentMethod.brand?.toUpperCase()}</span>
								</div>
								<div className="sf-payment-method-line">
									<span className="sf-payment-label">{t.paymentMethod.last4}</span>
									<span>•••• {billing.customer.paymentMethod.last4}</span>
								</div>
								<div className="sf-payment-method-line">
									<span className="sf-payment-label">{t.paymentMethod.expires}</span>
									<span>
										{billing.customer.paymentMethod.expMonth}/{billing.customer.paymentMethod.expYear}
									</span>
								</div>
							</div>
						) : (
							<div className="sf-empty-small">{t.paymentMethod.noStored}</div>
						)}
					</div>

					{/* Billing Dates */}
					{subscription && (
						<div className="sf-billing-card sf-sidebar-card">
							<h3 className="sf-sidebar-title">{t.billingInfo.title}</h3>
							<div className="sf-info-line">
								<span className="sf-info-label">{t.billingInfo.started}</span>
								<span>{new Date(subscription.startedAt).toLocaleDateString()}</span>
							</div>
							<div className="sf-info-line">
								<span className="sf-info-label">{t.billingInfo.firstBilled}</span>
								<span>{new Date(subscription.firstBillingDate).toLocaleDateString()}</span>
							</div>
							<div className="sf-info-line">
								<span className="sf-info-label">{t.billingInfo.nextBilling}</span>
								<span>{new Date(subscription.nextBillingDate).toLocaleDateString()}</span>
							</div>
							<div className="sf-info-line">
								<span className="sf-info-label">{t.billingInfo.interval}</span>
								<span>{t.billingInterval.replace("{frequency}", subscription.frequency).replace("{interval}", subscription.interval)}</span>
							</div>
						</div>
					)}

					{/* Referral Credits */}
					{billingReferral && (
						<div className="sf-billing-card sf-sidebar-card">
							<h3 className="sf-sidebar-title">{t.referralCredits.title}</h3>

							<div className="sf-credit-display">
								<span className="sf-credit-amount">${billingReferral.creditBalance.toFixed(2)}</span>
								<span className="sf-credit-sub">{t.referralCredits.availableCredit}</span>
							</div>

							<div className="sf-info-line" style={{ marginTop: 10 }}>
								<span className="sf-info-label">{t.referralCredits.rewardsEarned}</span>
								<span>
									{billingReferral.totalRewardCount}{" "}
									{billingReferral.totalRewardCount !== 1 ? t.referralCredits.referrals : t.referralCredits.referral}
								</span>
							</div>

							{billingReferral.usedReferralCode && (
								<div className="sf-referral-used-block">
									<div className="sf-info-label" style={{ marginBottom: 6 }}>
										{t.referralCredits.usedCode}
									</div>
									<div className="sf-info-line">
										<span className="sf-info-label">{t.referralCredits.code}</span>
										<span className="sf-code-pill">{billingReferral.usedReferralCode.code}</span>
									</div>
									<div className="sf-info-line">
										<span className="sf-info-label">{t.referralCredits.status}</span>
										<span className={`sf-badge sf-badge-${billingReferral.usedReferralCode.rewardIssued ? "active" : "pending"}`}>
											{billingReferral.usedReferralCode.rewardIssued
												? t.referralCredits.rewarded
												: t.referralCredits.statusLabels[billingReferral.usedReferralCode.status] ||
													billingReferral.usedReferralCode.status}
										</span>
									</div>
								</div>
							)}

							{billingReferral.referralsIssued?.length > 0 && (
								<div className="sf-referral-history" style={{ marginTop: 12 }}>
									<div className="sf-referral-history-title">{t.referralCredits.shopsReferred}</div>
									{billingReferral.referralsIssued.map((r, i) => (
										<div key={i} className="sf-referral-history-item">
											<span>{r.refereeEmail || r.refereeShopId}</span>
											<span className={`sf-badge sf-badge-${r.rewardIssued ? "active" : "pending"}`}>
												{r.rewardIssued ? t.referralCredits.rewarded : t.referralCredits.statusLabels[r.status] || r.status}
											</span>
										</div>
									))}
								</div>
							)}
						</div>
					)}

					{/* Referral Program */}
					<div className="sf-billing-card sf-sidebar-card">
						<h3 className="sf-sidebar-title">{t.referral.title}</h3>

						{refLoading ? (
							<div className="sf-empty-small">{t.referral.loading}</div>
						) : referral?.referralCode ? (
							<>
								<div className="sf-referral-block">
									<div className="sf-referral-label">{t.referral.codeLabel}</div>
									<div className="sf-referral-value-box">
										<span>{referral.referralCode}</span>
										<button
											className="sf-referral-copy-btn"
											onClick={() => {
												navigator.clipboard.writeText(referral.referralCode);
												showBanner("success", t.referral.copied);
											}}
										>
											{t.referral.copyCode}
										</button>
									</div>
								</div>

								<div className="sf-referral-block">
									<div className="sf-referral-label">{t.referral.linkLabel}</div>
									<div className="sf-referral-value-box">
										<span className="sf-referral-link">reservasinfila.com/listyourshop/{referral.referralCode}</span>
										<button
											className="sf-referral-copy-btn"
											onClick={() => {
												navigator.clipboard.writeText(`https://reservasinfila.com/listyourshop/${referral.referralCode}`);
												showBanner("success", t.referral.linkCopied);
											}}
										>
											{t.referral.copyLink}
										</button>
									</div>
								</div>
							</>
						) : (
							<>
								<div className="sf-empty-small">{t.referral.noCode}</div>
								<div className="sf-billing-referral-button-div">
									<button className="sf-btn sf-btn-primary" onClick={generateCode}>
										{t.referral.generate}
									</button>
								</div>
							</>
						)}
					</div>
				</div>
			</div>

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default AccountBillingPage;
